"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.createClient = createClient;
var _index = require("../../http/index.cjs");
var _routing = require("../../routing.cjs");
var _index2 = require("../../types/index.cjs");
var _types = require("./types.cjs");
/**
 * `uuid` is the preferred way of targeting a Story. Numeric `id` is considered deprecated.
 */

/**
 * Utility type to forbid arbitrary ad-hoc extensions of generic parameters.
 * @see https://stackoverflow.com/a/69666350
 */

function createClient(api) {
  async function list(options) {
    const {
      search,
      limit,
      offset,
      sortOrder,
      include,
      formats
    } = options ?? {};
    return api.get(_routing.routing.storiesUrl, {
      headers: acceptedFormatsHeader(formats),
      query: {
        search,
        limit,
        offset,
        sort: _index2.SortOrder.stringify(sortOrder),
        include: include ? include.join(',') : undefined
      }
    });
  }
  async function search(options) {
    const {
      search,
      limit,
      offset,
      sortOrder,
      include,
      query,
      formats
    } = options ?? {};
    return api.post(_routing.routing.storiesSearchUrl, {
      headers: acceptedFormatsHeader(formats),
      payload: {
        search,
        query,
        limit,
        offset,
        sort: _index2.SortOrder.stringify(sortOrder),
        include: include ? include.join(',') : undefined
      }
    });
  }

  /**
   * Get story by UUID.
   */

  /**
   * Get multiple stories by UUIDs.
   */

  /**
   * Get story by deprecated numeric ID, or UUID.
   * @deprecated Please use UUID instead.
   */

  /**
   * Get multiple stories by numeric IDs.
   * @deprecated Please use UUID instead.
   */

  /**
   * Implementation
   */
  async function get(arg, options) {
    const {
      include,
      formats
    } = options ?? {};
    const isArray = Array.isArray(arg);
    if (isArray && arg.length === 0) {
      // No need to call the API.
      return [];
    }
    if (isArray && typeof arg[0] === 'number') {
      const {
        stories
      } = await search({
        limit: arg.length,
        query: {
          id: {
            $in: arg
          }
        },
        include: [..._types.EXTENDED_STORY_INCLUDED_EXTRA_FIELDS, ...(include ?? [])],
        formats
      });
      return stories;
    }
    if (isArray && typeof arg[0] === 'string') {
      const {
        stories
      } = await search({
        limit: arg.length,
        query: {
          uuid: {
            $in: arg
          }
        },
        include: [..._types.EXTENDED_STORY_INCLUDED_EXTRA_FIELDS, ...(include ?? [])],
        formats
      });
      return stories;
    }
    const {
      story
    } = await api.get(`${_routing.routing.storiesUrl}/${arg}`, {
      headers: acceptedFormatsHeader(formats),
      query: {
        include
      }
    });
    return story;
  }
  async function getBySlug(slug, options) {
    if (slug.includes('/') || slug.includes('\\')) {
      throw new Error('Story slugs cannot contain slashes.');
    }
    const {
      include,
      query,
      formats
    } = options ?? {};
    const {
      story
    } = await api.post(`${_routing.routing.storiesUrl}/by-slug/${slug}`, {
      headers: acceptedFormatsHeader(formats),
      query: {
        include
      },
      payload: {
        query
      }
    });
    return story;
  }
  async function create(payload, options) {
    const {
      include,
      formats,
      force = false
    } = options ?? {};
    const {
      story
    } = await api.post(_routing.routing.storiesUrl, {
      headers: acceptedFormatsHeader(formats),
      query: {
        include,
        force: force || undefined // only pass it if it's true
      },
      payload
    });
    return story;
  }
  async function duplicate(id, options) {
    const {
      include,
      formats
    } = options ?? {};
    const url = `${_routing.routing.storiesUrl}/${id}/duplicate`;
    const {
      story
    } = await api.post(url, {
      headers: acceptedFormatsHeader(formats),
      query: {
        include
      }
    });
    return story;
  }
  async function translate(id, payload = {}, options) {
    const {
      culture
    } = payload ?? {};
    const {
      include,
      formats
    } = options ?? {};
    const url = `${_routing.routing.storiesUrl}/${id}/translate`;
    const {
      story
    } = await api.post(url, {
      headers: acceptedFormatsHeader(formats),
      query: {
        include
      },
      payload: {
        culture
      }
    });
    return story;
  }
  async function move(id, payload, options) {
    const {
      include,
      formats,
      force = false
    } = options ?? {};
    const url = `${_routing.routing.storiesUrl}/${id}/move`;
    try {
      const {
        story
      } = await api.post(url, {
        headers: acceptedFormatsHeader(formats),
        query: {
          include,
          force: force || undefined // only pass it if it's true
        },
        payload
      });
      return {
        status: 'success',
        story
      };
    } catch (error) {
      if (error instanceof _index.ApiError && error.status === _index.HttpCodes.CONFLICT) {
        return error.payload;
      }
      throw error;
    }
  }
  async function update(id, payload, options) {
    const url = `${_routing.routing.storiesUrl}/${id}`;
    const {
      include,
      formats,
      force = false
    } = options ?? {};
    const {
      story
    } = await api.patch(url, {
      headers: acceptedFormatsHeader(formats),
      query: {
        include,
        force: force || undefined // only pass it if it's true
      },
      payload
    });
    return story;
  }

  /**
   * This operation is only allowed for V3 stories
   */
  async function autosave(id, payload, options) {
    const url = `${_routing.routing.storiesUrl}/${id}/autosave`;
    const {
      include,
      formats
    } = options ?? {};
    const {
      story
    } = await api.patch(url, {
      headers: acceptedFormatsHeader(formats),
      payload,
      query: {
        include
      }
    });
    return story;
  }

  /**
   * This operation is only allowed for V3 stories
   */
  async function revert(id, payload, options) {
    const url = `${_routing.routing.storiesUrl}/${id}/revert`;
    const {
      include,
      formats
    } = options ?? {};
    const {
      story
    } = await api.post(url, {
      headers: acceptedFormatsHeader(formats),
      payload,
      query: {
        include
      }
    });
    return story;
  }
  async function publish(id, payload, options) {
    const url = `${_routing.routing.storiesUrl}/${id}/publish`;
    const {
      include,
      formats
    } = options ?? {};
    const {
      story
    } = await api.post(url, {
      headers: acceptedFormatsHeader(formats),
      payload,
      query: {
        include
      }
    });
    return story;
  }
  async function unpublish(id, payload, options) {
    const url = `${_routing.routing.storiesUrl}/${id}/unpublish`;
    const {
      include,
      formats
    } = options ?? {};
    const {
      story
    } = await api.post(url, {
      headers: acceptedFormatsHeader(formats),
      payload,
      query: {
        include
      }
    });
    return story;
  }
  async function schedule(id, payload, options) {
    const url = `${_routing.routing.storiesUrl}/${id}/schedule`;
    const {
      include,
      formats
    } = options ?? {};
    const {
      story
    } = await api.post(url, {
      headers: acceptedFormatsHeader(formats),
      payload,
      query: {
        include
      }
    });
    return story;
  }
  async function unschedule(id, payload, options) {
    const url = `${_routing.routing.storiesUrl}/${id}/unpublish`;
    const {
      include,
      formats
    } = options ?? {};
    const {
      story
    } = await api.post(url, {
      headers: acceptedFormatsHeader(formats),
      payload,
      query: {
        include
      }
    });
    return story;
  }
  async function pin(id, options) {
    const url = `${_routing.routing.storiesUrl}/${id}/pin`;
    const {
      include,
      formats,
      force = false
    } = options ?? {};
    const {
      story
    } = await api.post(url, {
      headers: acceptedFormatsHeader(formats),
      query: {
        include: include,
        force: force || undefined // only pass it if it's true
      }
    });
    return story;
  }
  async function unpin(id, options) {
    const url = `${_routing.routing.storiesUrl}/${id}/unpin`;
    const {
      include,
      formats
    } = options ?? {};
    const {
      story
    } = await api.post(url, {
      headers: acceptedFormatsHeader(formats),
      query: {
        include: include
      }
    });
    return story;
  }
  async function doDelete(id) {
    const url = `${_routing.routing.storiesUrl}/${id}`;
    await api.delete(url);
  }
  async function preview(id, options) {
    const url = `${_routing.routing.storiesUrl}/${id}/preview`;
    const {
      preview
    } = await api.get(url, {
      query: {
        alignment: options?.alignment,
        appearance: options?.appearance,
        header_footer: options?.headerFooter
      }
    });
    return preview;
  }
  return {
    list,
    search,
    get,
    getBySlug,
    create,
    duplicate,
    translate,
    move,
    update,
    autosave,
    revert,
    publish,
    unpublish,
    schedule,
    unschedule,
    pin,
    unpin,
    delete: doDelete,
    preview
  };
}
function acceptedFormatsHeader(formats = []) {
  if (formats.length === 0) {
    return {};
  }
  return {
    Accept: formats.map(format => `application/json; content-format=v${format}`).join(', ')
  };
}